import React from "react";
import { Box, Button } from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";
import type { Training } from "../../../api/training/Training.typed";
import { SHistory, SQuiz } from "../../../styles/StyledMUI";
import DarkInput from "../../common/DarkInput";
import DarkTypography from "../../common/DarkTypography";
import {
  colorTokens,
  ctaAccentEnabledSx,
  ctaIconButtonDisabledSx,
} from "../../../styles/globalStyles";

const resultsButtonSx: SxProps = [
  ctaAccentEnabledSx,
  ctaIconButtonDisabledSx,
  { borderRadius: "8px", alignSelf: { xs: "flex-end", sm: "center" } },
] as unknown as SxProps;
import TheoryTestResultsModal from "./display/TheoryTestResultsModal";
import { useTheoryTestTemplate } from "./hooks/useTheoryTestTemplate";
import { theoryTestResultFraction } from "./theoryTestHelpers";

export type TheoryTestPanelProps = {
  training: Training;
  testResult: string;
  setTestResult?: React.Dispatch<React.SetStateAction<string>>;
  updateTraining?: (training: Training) => Promise<void>;
  getTrainingsByTemplateId?: (templateId: string) => Promise<Training[] | undefined>;
  setTrainings?: React.Dispatch<React.SetStateAction<Training[] | undefined>>;
  isOpenTheoryTests?: boolean;
  status?: number;
  standalone?: boolean;
};

export default function TheoryTestPanel({
  training,
  testResult,
  standalone,
}: TheoryTestPanelProps) {
  const hasOldTest = Boolean(testResult);
  const hasNewTests = training.TheoryTests && training.TheoryTests.length > 0;
  const hasAnyTest = hasOldTest || hasNewTests;

  const [resultsModalOpen, setResultsModalOpen] = React.useState(false);
  const [selectedTab, setSelectedTab] = React.useState(0);
  const { template, tests } = useTheoryTestTemplate(training);

  const titleLine = [
    "QCM",
    [training.FirstName, training.LastName].filter(Boolean).join(" "),
    training.Qualification,
  ]
    .filter(Boolean)
    .join(" - ");

  const content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        gap: 1,
        marginTop: 1,
        marginBottom: 1,
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
        <SQuiz />
      </Box>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "8px",
          padding: 2,
          textAlign: "left",
          width: "100%",
          minWidth: 0,
          backgroundColor: colorTokens.bg.surface,
        }}
      >
        {!hasAnyTest ? (
          <DarkTypography level="body-md" sx={{ textAlign: "left" }}>
            Aucun QCM réalisé
          </DarkTypography>
        ) : (
          <>
            {hasOldTest && (
              <>
                <DarkTypography level="title-lg" sx={{ mb: 2 }}>
                  QCM (ancienne version)
                </DarkTypography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <DarkTypography level="body-md">Note finale :</DarkTypography>
                  <DarkInput
                    value={testResult}
                    endDecorator="%"
                    sx={{
                      width: "80px",
                      "--Input-decoratorChildHeight": "24px",
                    }}
                    disabled
                  />
                </Box>
              </>
            )}

            {hasNewTests && tests.length > 0 && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    gap: { xs: 0.5, sm: 1 },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "flex-start", sm: "center" },
                      gap: { xs: 0.5, sm: 2 },
                    }}
                  >
                    <DarkTypography
                      level="body-md"
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                    >
                      Tentatives : {tests.length}
                    </DarkTypography>
                    <DarkTypography
                      level="body-md"
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                    >
                      Meilleure note :{" "}
                      {theoryTestResultFraction(
                        Math.max(...tests.map((t) => t.Result), 0),
                      ).toLocaleString("fr-FR", {
                        style: "percent",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </DarkTypography>
                  </Box>
                  <Button
                    size="sm"
                    variant="soft"
                    onClick={() => setResultsModalOpen(true)}
                    aria-label="Voir résultats QCM"
                    startDecorator={<SHistory sx={{ fontSize: "1.1rem" }} />}
                    sx={resultsButtonSx}
                  >
                    Voir résultats
                  </Button>
                </Box>

                <TheoryTestResultsModal
                  open={resultsModalOpen}
                  onClose={() => setResultsModalOpen(false)}
                  titleLine={titleLine}
                  tests={tests}
                  template={template}
                  selectedTab={selectedTab}
                  onTabChange={setSelectedTab}
                />
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );

  if (standalone) {
    return <Box sx={{ py: 1, width: "100%", minWidth: 0 }}>{content}</Box>;
  }
  return (
    <tr>
      <td colSpan={12} style={{ border: "none" }}>
        {content}
      </td>
    </tr>
  );
}
